import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ManagedCampaignData, UploadedTarget } from "@/components/managed-campaign/ManagedCampaignData";
import { ManagedGeographyStep } from "@/components/managed-campaign/ManagedGeographyStep";
import { ManagedIndustryStep } from "@/components/managed-campaign/ManagedIndustryStep";
import { ManagedPeopleStep } from "@/components/managed-campaign/ManagedPeopleStep";
import { ManagedKeywordsStep } from "@/components/managed-campaign/ManagedKeywordsStep";
import { ManagedSequenceStep } from "@/components/managed-campaign/ManagedSequenceStep";
import { AiCampaignStrategist } from "./AiCampaignStrategist";
import { TargetAcquisitionSelector, TargetAcquisitionMode, TARGET_MODE_LABELS } from "./TargetAcquisitionSelector";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { GuestSalesNavImporter } from "./GuestSalesNavImporter";

import { InlineAudienceBuilder } from "./InlineAudienceBuilder";
import { ExclusionMatchWarning } from "./ExclusionMatchWarning";
import { CSVUploader } from "@/components/campaigns/CSVUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles, RotateCcw, Users, ExternalLink, Trash2, MapPin, Building2, Hash, SlidersHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";

interface AiGenerationProps {
    companyProfileId: string;
    campaignKnowledgeId: string;
    hasGenerated: boolean;
    onGenerated: (data: Partial<ManagedCampaignData>) => void;
}

interface SimplifiedCampaignStepProps {
    data: ManagedCampaignData;
    updateData: (updates: Partial<ManagedCampaignData>) => void;
    onNext: () => void;
    onBack: () => void;
    mode?: "audience" | "messaging";
    onReset?: () => void;
    onResetStep?: () => void; // Reset only this step's data
    hideAiStrategist?: boolean;
    // For target acquisition (guest onboarding)
    showTargetAcquisition?: boolean;
    clientId?: string;
    clientName?: string;
    guestToken?: string;
    // For AI generation in audience mode (when in Define Criteria)
    aiGenerationProps?: AiGenerationProps;
}

export const SimplifiedCampaignStep = ({ 
    data, 
    updateData, 
    onNext, 
    onBack, 
    mode = "audience", 
    onReset, 
    onResetStep, 
    hideAiStrategist = false,
    showTargetAcquisition = false,
    clientId,
    clientName,
    guestToken,
    aiGenerationProps,
}: SimplifiedCampaignStepProps) => {
    // If in messaging mode, always active "messaging". If audience, start with "geography"
    const [activeTab, setActiveTab] = useState(mode === "messaging" ? "messaging" : "geography");
    const [isAiOpen, setIsAiOpen] = useState(false);
    const { workspace } = useWorkspace();

    // Check if AI Assistant is enabled by super admin
    const isAiEnabled = true;

    // In the onboarding flow (showTargetAcquisition) nothing is chosen until the user picks a method.
    const modeChosen = !showTargetAcquisition || !!data.targetAcquisitionMode;
    const targetAcquisitionMode = data.targetAcquisitionMode || "automatic";
    const uploadedTargets = data.uploadedTargets || [];

    const isGeographyComplete = data.countries.length > 0 || data.cities.length > 0;
    const isIndustryComplete = data.industries.length > 0 && data.companySizes.length > 0;
    const isPeopleComplete = data.jobTitles.length > 0;
    // Keywords are optional - always show as complete (no blocking indicator)
    const isKeywordsComplete = true;
    const isMessagingComplete = data.sequences.length > 0;

    // For CSV/Sales Nav modes, check if targets are uploaded
    const hasUploadedTargets = uploadedTargets.length > 0;

    // Define available tabs based on mode
    const tabs = mode === "messaging"
        ? ["messaging"]
        : ["geography", "industry", "people", "keywords"];

    const handleTabChange = (val: string) => {
        setActiveTab(val);
    };

    const handleContinue = () => {
        // For CSV mode, check if targets are uploaded
        if (mode === "audience" && targetAcquisitionMode === "csv") {
            if (hasUploadedTargets) {
                onNext();
            }
            return;
        }
        
        // For Sales Nav mode: guests need URL; authenticated need imported targets
        if (mode === "audience" && targetAcquisitionMode === "sales_nav") {
            const authedOk = !guestToken && hasUploadedTargets;
            const guestOk = !!guestToken && !!data.salesNavUrl?.trim();
            if (authedOk || guestOk) {
                onNext();
            }
            return;
        }

        if (mode === "audience" && showTargetAcquisition) { if (isGeographyComplete) onNext(); return; }
        // Check active tab index
        const index = tabs.indexOf(activeTab);
        if (index < tabs.length - 1) {
            setActiveTab(tabs[index + 1]);
        } else {
            onNext();
        }
    };

    const handleBack = () => {
        // For CSV/Sales Nav modes, go back directly
        if (mode === "audience" && (targetAcquisitionMode === "csv" || targetAcquisitionMode === "sales_nav")) {
            onBack();
            return;
        }

        const index = tabs.indexOf(activeTab);
        if (index > 0) {
            setActiveTab(tabs[index - 1]);
        } else {
            onBack();
        }
    };

    const criteriaCount = data.countries.length + data.cities.length + data.industries.length + data.jobTitles.length + (data.companyKeywords?.length || 0) + (data.personKeywords?.length || 0);
    // Every visit to the Audience step starts from the three cards when nothing has been added yet.
    useEffect(() => {
        if (mode !== "audience" || !showTargetAcquisition) return;
        const hasAny = criteriaCount > 0 || hasUploadedTargets || !!data.salesNavUrl?.trim() || !!aiGenerationProps?.hasGenerated;
        if (!hasAny && data.targetAcquisitionMode) updateData({ targetAcquisitionMode: undefined });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const modeHasData = (m: TargetAcquisitionMode) => {
        if (m === "csv") return hasUploadedTargets;
        if (m === "sales_nav") return !!data.salesNavUrl?.trim() || hasUploadedTargets;
        return criteriaCount > 0;
    };
    const summaries: Partial<Record<TargetAcquisitionMode, string>> = {};
    if (modeChosen) {
        if (targetAcquisitionMode === "csv" && hasUploadedTargets) summaries.csv = `${uploadedTargets.length} people added`;
        if (targetAcquisitionMode === "sales_nav") {
            if (hasUploadedTargets) summaries.sales_nav = `${uploadedTargets.length} people added`;
            else if (data.salesNavUrl?.trim()) summaries.sales_nav = "Search link added";
        }
        if (targetAcquisitionMode === "automatic" && criteriaCount > 0) {
            const parts = [[...data.countries, ...data.cities][0], data.industries[0], data.jobTitles.length ? `${data.jobTitles.length} titles` : null].filter(Boolean);
            summaries.automatic = parts.join(" · ") || "Filters added";
        }
    }
    const [pendingMode, setPendingMode] = useState<TargetAcquisitionMode | null>(null);

    const applyMode = (newMode: TargetAcquisitionMode) => {
        updateData({
            targetAcquisitionMode: newMode,
            uploadedTargets: [],
            salesNavUrl: "",
            ...(targetAcquisitionMode === "automatic" && newMode !== "automatic"
                ? { countries: [], cities: [], industries: [], jobTitles: [], companyKeywords: [], personKeywords: [] }
                : {}),
        });
    };

    const handleTargetModeChange = (newMode: TargetAcquisitionMode) => {
        if (modeChosen && newMode === targetAcquisitionMode) return;
        if (modeChosen && modeHasData(targetAcquisitionMode)) { setPendingMode(newMode); return; }
        if (!modeChosen) { updateData({ targetAcquisitionMode: newMode }); return; }
        applyMode(newMode);
    };

    const handleCSVUpload = (targets: any[], headers: string[]) => {
        const uploadedTargets: UploadedTarget[] = targets.map(t => ({
            linkedin_url: t.linkedin_url,
            profile_url: t.profile_url,
            first_name: t.first_name,
            last_name: t.last_name,
            company: t.company,
            title: t.title,
            provider_id: t.provider_id,
            sales_nav_url: t.sales_nav_url,
            custom_data: t.custom_data,
            name: t.name,
            link_type: t.link_type,
        }));
        updateData({ uploadedTargets });
    };

    const handleSalesNavUrlChange = (url: string) => {
        updateData({ salesNavUrl: url });
    };

    const clearSalesNavUrl = () => {
        updateData({ salesNavUrl: "" });
    };

    const clearUploadedTargets = () => {
        updateData({ uploadedTargets: [] });
    };

    // Check if we can proceed based on mode
    const canProceed = () => {
        if (mode === "audience" && !modeChosen) return false;
        if (mode === "messaging") {
            return data.sequences.filter(s => s.action_type !== "end").length > 0;
        }

        if (targetAcquisitionMode === "csv") {
            return hasUploadedTargets;
        }
        
        if (targetAcquisitionMode === "sales_nav") {
            return guestToken ? !!data.salesNavUrl?.trim() : hasUploadedTargets;
        }

        if (showTargetAcquisition) return isGeographyComplete;
        // Automatic mode - validate per-tab when not on last tab
        const index = tabs.indexOf(activeTab);
        if (index < tabs.length - 1) {
            // Not on the last tab - allow proceeding to next tab freely
            return true;
        }

        // On last tab (keywords) - require all criteria before final "Next Step"
        return isGeographyComplete && isIndustryComplete && isPeopleComplete;
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="space-y-1">
                    <h2 className="text-2xl font-bold tracking-tight">
                        {mode === "messaging" ? "Messaging Strategy" : "Target Audience"}
                    </h2>
                    <p className="text-muted-foreground">
                        {mode === "messaging"
                            ? "Define the sequences and connection messages for your campaign."
                            : "Choose how we find the right people."}
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    {onResetStep && !(mode === "audience" && showTargetAcquisition) && (
                        <Button
                            onClick={onResetStep}
                            variant="outline"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            title={mode === "messaging" ? "Reset messages" : "Reset audience"}
                        >
                            <RotateCcw className="w-4 h-4" />
                        </Button>
                    )}
                    {isAiEnabled && !hideAiStrategist && (
                        <Button
                            onClick={() => setIsAiOpen(true)}
                            className="bg-gradient-to-r from-primary to-primary hover:from-primary hover:to-primary text-white shadow-md hover:shadow-lg transition-all"
                        >
                            <Sparkles className="w-4 h-4 mr-2" />
                            Ask AI Strategist
                        </Button>
                    )}
                </div>
            </div>

            {/* Target Acquisition: options always visible, one method at a time */}
            {mode === "audience" && showTargetAcquisition && (
                <div className="space-y-3">
                    <TargetAcquisitionSelector
                        selectedMode={modeChosen ? targetAcquisitionMode : null}
                        onSelect={handleTargetModeChange}
                        summaries={summaries}
                        compact={modeChosen}
                    />
                    {modeChosen && onResetStep && (
                        <div className="flex justify-end">
                            <Button variant="ghost" size="sm" onClick={onResetStep} className="text-muted-foreground hover:text-destructive">
                                <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset
                            </Button>
                        </div>
                    )}
                </div>
            )}
            <AlertDialog open={!!pendingMode} onOpenChange={(o) => !o && setPendingMode(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Switch to {pendingMode ? TARGET_MODE_LABELS[pendingMode] : ""}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You can use only one method. This clears {summaries[targetAcquisitionMode] ? `"${summaries[targetAcquisitionMode]}"` : "what you added"} from {TARGET_MODE_LABELS[targetAcquisitionMode]}.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Keep current</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { if (pendingMode) applyMode(pendingMode); setPendingMode(null); }}>
                            Clear and switch
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* CSV Upload Mode */}
            {mode === "audience" && showTargetAcquisition && modeChosen && targetAcquisitionMode === "csv" && (
                <div className="space-y-4">
                    {/* Only show CSVUploader when no targets uploaded yet */}
                    {!hasUploadedTargets && (
                        <CSVUploader
                            onUpload={handleCSVUpload}
                            uploadedCount={uploadedTargets.length}
                        />
                    )}

                    {/* Uploaded Targets Preview */}
                    {hasUploadedTargets && (
                        <>
                        <ExclusionMatchWarning
                            targets={uploadedTargets}
                            onRemove={(kept) => updateData({ uploadedTargets: kept })}
                            extraCompany={data.excludeCompanyKeywords || []}
                            extraPerson={data.excludePersonKeywords || []}
                            companyProfileId={aiGenerationProps?.companyProfileId}
                            campaignKnowledgeId={aiGenerationProps?.campaignKnowledgeId}
                            guestToken={guestToken}
                            workspaceId={workspace?.id ?? null}
                        />
                        <UploadedTargetsPreview
                            targets={uploadedTargets}
                            onClear={clearUploadedTargets}
                            onUploadDifferent={() => {
                                clearUploadedTargets();
                            }}
                        />
                        </>
                    )}
                </div>
            )}

            {/* Sales Navigator Mode */}
            {mode === "audience" && showTargetAcquisition && modeChosen && targetAcquisitionMode === "sales_nav" && (
                <>
                    {/* Guests: just capture the URL - admin imports later via submit_guest_campaign */}
                    {guestToken ? (
                        <SalesNavUrlInput
                            url={data.salesNavUrl || ""}
                            onChange={handleSalesNavUrlChange}
                            onClear={clearSalesNavUrl}
                        />
                    ) : (
                        /* Authenticated: run a real search and import into uploadedTargets */
                        !hasUploadedTargets ? (
                            <GuestSalesNavImporter
                                clientId={clientId || data.clientId}
                                clientName={clientName}
                                onImport={handleCSVUpload}
                                uploadedCount={uploadedTargets.length}
                            />
                        ) : (
                            <div className="space-y-4">
                            <ExclusionMatchWarning
                            targets={uploadedTargets}
                            onRemove={(kept) => updateData({ uploadedTargets: kept })}
                            extraCompany={data.excludeCompanyKeywords || []}
                            extraPerson={data.excludePersonKeywords || []}
                            companyProfileId={aiGenerationProps?.companyProfileId}
                            campaignKnowledgeId={aiGenerationProps?.campaignKnowledgeId}
                            guestToken={guestToken}
                            workspaceId={workspace?.id ?? null}
                        />
                        <UploadedTargetsPreview
                                targets={uploadedTargets}
                                onClear={clearUploadedTargets}
                                onUploadDifferent={clearUploadedTargets}
                            />
                            </div>
                        )
                    )}
                </>
            )}

            {/* Automatic Mode - Original Tabs UI */}
            {mode === "audience" && modeChosen && (!showTargetAcquisition || targetAcquisitionMode === "automatic") && (
                <div className="space-y-6">
                    {showTargetAcquisition && (
                        <InlineAudienceBuilder
                            data={data}
                            updateData={updateData}
                            companyProfileId={aiGenerationProps?.companyProfileId}
                            campaignKnowledgeId={aiGenerationProps?.campaignKnowledgeId}
                            guestToken={guestToken}
                            workspaceId={workspace?.id ?? null}
                            onAiApplied={() => aiGenerationProps?.onGenerated({})}
                            compact
                        />
                    )}

                    {(
                    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-xl bg-muted/60 p-1 sm:grid-cols-4">
                            {([
                                ["geography", "Geography", MapPin, isGeographyComplete],
                                ["industry", "Industry", Building2, isIndustryComplete],
                                ["people", "People", Users, isPeopleComplete],
                                ["keywords", "Keywords", Hash, data.companyKeywords.length + data.personKeywords.length > 0],
                            ] as const).map(([v, label, Icon, done]) => (
                                <TabsTrigger key={v} value={v} className="relative gap-2 rounded-lg py-2.5 data-[state=active]:shadow-sm">
                                    <Icon className="h-4 w-4" />
                                    {label}
                                    {done && <span className="h-2 w-2 rounded-full bg-sage" aria-label="filled" />}
                                </TabsTrigger>
                            ))}
                        </TabsList>

                        <div className="mt-6 rounded-2xl border bg-card p-5 shadow-sm sm:p-6">
                            <TabsContent value="geography" className="mt-0 space-y-4">
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Where</h3>
                                <ManagedGeographyStep data={data} updateData={updateData} />
                            </TabsContent>
                            <TabsContent value="industry" className="mt-0 space-y-4">
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Industries & size</h3>
                                <ManagedIndustryStep data={data} updateData={updateData} />
                            </TabsContent>
                            <TabsContent value="people" className="mt-0 space-y-4">
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Roles</h3>
                                <ManagedPeopleStep data={data} updateData={updateData} />
                            </TabsContent>
                            <TabsContent value="keywords" className="mt-0 space-y-4">
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Keywords</h3>
                                <ManagedKeywordsStep data={data} updateData={updateData} />
                            </TabsContent>
                        </div>
                    </Tabs>
                    )}
                </div>
            )}

            {/* Messaging Mode */}
            {mode === "messaging" && (
                <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                    <div className="mt-6">
                        <TabsContent value="messaging" className="mt-0 space-y-4">
                            <ManagedSequenceStep data={data} updateData={updateData} isPremium={data.isPremium || false} />
                        </TabsContent>
                    </div>
                </Tabs>
            )}

            <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={handleBack} className="gap-2">
                    <ArrowLeft className="h-4 w-4" />
                    Back
                </Button>
                {!(mode === "audience" && !modeChosen) && <Button onClick={handleContinue} className="gap-2" disabled={!canProceed()}>
                    {"Next Step"}
                    <ArrowRight className="h-4 w-4" />
                </Button>}
            </div>

            {isAiEnabled && !hideAiStrategist && (
                <AiCampaignStrategist 
                    open={isAiOpen} 
                    onOpenChange={setIsAiOpen} 
                    mode={mode} 
                    currentData={data}
                    onFieldUpdate={(field, value) => updateData({ [field]: value })}
                    workspaceId={workspace?.id || ""}
                />
            )}
        </div>
    );
};

// Helper component to preview uploaded targets
const UploadedTargetsPreview = ({ 
    targets, 
    onClear,
    onUploadDifferent
}: { 
    targets: UploadedTarget[]; 
    onClear: () => void;
    onUploadDifferent?: () => void;
}) => {
    return (
        <Card className="p-4 border-sage/30 bg-sage-muted/50 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-sage/15">
                        <Users className="w-5 h-5 text-sage" />
                    </div>
                    <div>
                        <h4 className="font-semibold text-foreground">
                            {targets.length} people added
                        </h4>
                        <p className="text-sm text-muted-foreground">
                            Ready for this campaign
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {onUploadDifferent && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onUploadDifferent}
                        >
                            Replace
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClear}
                        className="text-muted-foreground hover:text-destructive"
                    >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Clear
                    </Button>
                </div>
            </div>

            <div className="max-h-72 overflow-auto border rounded-md bg-background">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Company</TableHead>
                            <TableHead className="w-10"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {targets.slice(0, 30).map((target, idx) => (
                            <TableRow key={idx}>
                                <TableCell className="font-medium">
                                    {target.name || `${target.first_name || ''} ${target.last_name || ''}`.trim() || '-'}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground truncate max-w-[180px]">
                                    {target.title || "-"}
                                </TableCell>
                                <TableCell className="text-sm truncate max-w-[140px]">
                                    {target.company || "-"}
                                </TableCell>
                                <TableCell>
                                    {target.linkedin_url && (
                                        <a
                                            href={target.linkedin_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-muted-foreground hover:text-primary"
                                        >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                        </a>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {targets.length > 30 && (
                    <div className="text-center py-2 text-xs text-muted-foreground bg-muted/50">
                        Showing first 30 of {targets.length} targets
                    </div>
                )}
            </div>
        </Card>
    );
};

// Helper component for Sales Navigator URL input
const SalesNavUrlInput = ({ 
    url, 
    onChange, 
    onClear 
}: { 
    url: string; 
    onChange: (url: string) => void;
    onClear: () => void;
}) => {
    const isValidUrl = url.includes('linkedin.com/sales');
    const hasUrl = url.trim().length > 0;

    return (
        <Card className={`p-6 border-2 ${hasUrl && isValidUrl ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-dashed'}`}>
            <div className="space-y-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${hasUrl && isValidUrl ? 'bg-emerald-500/20' : 'bg-muted'}`}>
                        <ExternalLink className={`w-5 h-5 ${hasUrl && isValidUrl ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                        <h4 className="font-semibold">Sales Navigator Search Link</h4>
                        <p className="text-sm text-muted-foreground">
                            Paste your Sales Navigator search URL
                        </p>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="salesNavUrl" className="text-sm">
                        Search URL <span className="text-destructive">*</span>
                    </Label>
                    <div className="flex gap-2">
                        <Input
                            id="salesNavUrl"
                            value={url}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder="https://www.linkedin.com/sales/search/people?..."
                            className="flex-1"
                        />
                        {hasUrl && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onClear}
                                className="text-muted-foreground hover:text-destructive"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                    {hasUrl && !isValidUrl && (
                        <p className="text-xs text-destructive">
                            Please enter a valid Sales Navigator URL
                        </p>
                    )}
                    {hasUrl && isValidUrl && (
                        <p className="text-xs text-emerald-600">
                            ✓ Valid Sales Navigator link
                        </p>
                    )}
                </div>

                <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
                    <p className="font-medium mb-1">How to get this link:</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                        <li>Open LinkedIn Sales Navigator</li>
                        <li>Apply your search filters</li>
                        <li>Copy the URL from your browser</li>
                    </ol>
                </div>
            </div>
        </Card>
    );
};
